import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Event,
  Add,
  Cancel,
  CalendarMonth,
  AccessTime,
  LocalHospital,
  Person,
  Pets,
  Close,
  Visibility,
  Edit
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { clientDashboardService, ClientAppointment, ClientPatient, AvailabilitySlot } from '@services/clientDashboardService';
import { format, addDays, startOfWeek, endOfWeek, isSameDay, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  pending_confirmation: 'warning',
  scheduled: 'primary',
  confirmed: 'success',
  cancelled: 'error',
  completed: 'default',
  in_progress: 'warning',
  'in-progress': 'warning',
  no_show: 'error'
};

const statusLabels: Record<string, string> = {
  pending_confirmation: 'In asteptare',
  scheduled: 'Programat',
  confirmed: 'Confirmat',
  cancelled: 'Anulat',
  completed: 'Finalizat',
  in_progress: 'In desfasurare',
  'in-progress': 'In desfasurare',
  no_show: 'No-show'
};

const MyAppointments: React.FC = () => {
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(0);
  const [appointments, setAppointments] = useState<ClientAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog pentru programare noua
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [pets, setPets] = useState<ClientPatient[]>([]);
  const [selectedPet, setSelectedPet] = useState<string>('');
  const [selectedClinic, setSelectedClinic] = useState<string>('');
  const [selectedDoctor, setSelectedDoctor] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<AvailabilitySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Dialog pentru anulare
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<ClientAppointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Dialog pentru detalii
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAppointmentDetails, setSelectedAppointmentDetails] = useState<ClientAppointment | null>(null);

  // Dialog pentru reprogramare
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<ClientAppointment | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleSlotsAvailable, setRescheduleSlotsAvailable] = useState<AvailabilitySlot[]>([]);
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false);

  // Mock data pentru clinici si medici (in productie vin din API)
  const clinics = [
    { id: 'clinic1', name: 'VetClinic Nord', address: 'Str. Mihai Eminescu 10, Bucuresti' },
    { id: 'clinic2', name: 'VetClinic Sud', address: 'Bd. Unirii 45, Bucuresti' }
  ];

  const doctors = [
    { id: 'doc1', name: 'Dr. Ion Popescu', specialty: 'Medicina Generala' },
    { id: 'doc2', name: 'Dr. Maria Ionescu', specialty: 'Chirurgie' }
  ];

  const steps = ['Selecteaza animalul', 'Alege clinica si medicul', 'Selecteaza data si ora', 'Confirma'];

  useEffect(() => {
    loadAppointments();
    loadPets();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientDashboardService.getAppointments({ limit: 100 });
      setAppointments(data.data);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Eroare la incarcarea programarilor');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAppointmentRequest = () => {
    navigate('/client/clinics');
  };

  const loadPets = async () => {
    try {
      const data = await clientDashboardService.getPatients();
      setPets(data);
    } catch (err) {
      console.error('Error loading pets:', err);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedClinic || !selectedDoctor || !selectedDate) return;

    try {
      setLoadingSlots(true);
      const fromDate = new Date(selectedDate);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(selectedDate);
      toDate.setHours(23, 59, 59, 999);

      const slots = await clientDashboardService.getAvailability({
        clinic_id: selectedClinic,
        doctor_id: selectedDoctor,
        from: fromDate.toISOString(),
        to: toDate.toISOString()
      });
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Error loading slots:', err);
      alert('Eroare la incarcarea sloturilor disponibile');
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (selectedDate && selectedClinic && selectedDoctor) {
      loadAvailableSlots();
    }
  }, [selectedDate, selectedClinic, selectedDoctor]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCreateAppointment = async () => {
    try {
      await clientDashboardService.createAppointment({
        patient_id: selectedPet,
        clinic_id: selectedClinic,
        doctor_id: selectedDoctor,
        start_ts: selectedTime,
        reason: reason || 'Consultatie'
      });

      setCreateDialogOpen(false);
      resetCreateDialog();
      await loadAppointments();
      alert('Programare creata cu succes!');
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      alert(err.response?.data?.error || 'Eroare la crearea programarii');
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;

    try {
      await clientDashboardService.cancelAppointment(appointmentToCancel.id, cancelReason);
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setCancelReason('');
      await loadAppointments();
      alert('Programare anulata cu succes!');
    } catch (err: any) {
      console.error('Error cancelling appointment:', err);
      alert(err.response?.data?.error || 'Eroare la anularea programarii');
    }
  };

  const handleOpenDetails = (appointment: ClientAppointment) => {
    setSelectedAppointmentDetails(appointment);
    setDetailsDialogOpen(true);
  };

  const handleOpenReschedule = (appointment: ClientAppointment) => {
    setAppointmentToReschedule(appointment);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
    setRescheduleSlotsAvailable([]);
    setRescheduleDialogOpen(true);
  };

  const loadRescheduleSlots = async () => {
    if (!appointmentToReschedule || !rescheduleDate) return;

    try {
      setLoadingRescheduleSlots(true);
      const fromDate = new Date(rescheduleDate);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(rescheduleDate);
      toDate.setHours(23, 59, 59, 999);

      const slots = await clientDashboardService.getAvailability({
        clinic_id: appointmentToReschedule.clinic.id,
        doctor_id: appointmentToReschedule.doctor.id,
        from: fromDate.toISOString(),
        to: toDate.toISOString()
      });
      setRescheduleSlotsAvailable(slots);
    } catch (err) {
      console.error('Error loading reschedule slots:', err);
      alert('Eroare la incarcarea sloturilor disponibile');
    } finally {
      setLoadingRescheduleSlots(false);
    }
  };

  const handleRequestReschedule = async () => {
    if (!appointmentToReschedule || !rescheduleTime) return;

    try {
      // Trimite cerere de reprogramare către clinică (nu reprogramează automat)
      // Backend-ul va crea o notificare pentru clinică
      await clientDashboardService.requestReschedule(
        appointmentToReschedule.id,
        {
          new_start_ts: rescheduleTime,
          reason: rescheduleReason
        }
      );

      setRescheduleDialogOpen(false);
      setAppointmentToReschedule(null);
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleReason('');
      alert('Cerere de reprogramare trimisa cu succes! Veti fi notificat cand clinica aproba cererea.');
    } catch (err: any) {
      console.error('Error requesting reschedule:', err);
      alert(err.response?.data?.error || 'Eroare la trimiterea cererii de reprogramare');
    }
  };

  useEffect(() => {
    if (rescheduleDate && appointmentToReschedule) {
      loadRescheduleSlots();
    }
  }, [rescheduleDate, appointmentToReschedule]);

  const resetCreateDialog = () => {
    setActiveStep(0);
    setSelectedPet('');
    setSelectedClinic('');
    setSelectedDoctor('');
    setSelectedDate('');
    setSelectedTime('');
    setReason('');
    setAvailableSlots([]);
  };

  const getFilteredAppointments = () => {
    const now = new Date();
    switch (tabValue) {
      case 0: // Viitoare
        return appointments.filter(apt => 
          new Date(apt.start_ts) >= now && 
          ['pending_confirmation', 'scheduled', 'confirmed'].includes(apt.status)
        );
      case 1: // In asteptare (in desfasurare)
        return appointments.filter(apt => ['in_progress', 'in-progress'].includes(apt.status));
      case 2: // Istoric (completate + anulate)
        return appointments.filter(apt => 
          ['completed', 'cancelled'].includes(apt.status) ||
          (new Date(apt.start_ts) < now && apt.status !== 'in-progress')
        );
      default:
        return appointments;
    }
  };

  const canCancelAppointment = (appointment: ClientAppointment): boolean => {
    const appointmentDate = new Date(appointment.start_ts);
    const now = new Date();
    const hoursUntil = (appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntil >= 24 && ['pending_confirmation', 'scheduled', 'confirmed'].includes(appointment.status);
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Programarile Mele
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleStartAppointmentRequest}
          disabled={pets.length === 0}
        >
          Alege clinica pentru programare
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {pets.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Trebuie sa adaugi mai intai un animal pentru a putea face programari.
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Viitoare" />
            <Tab label="In asteptare" />
            <Tab label="Istoric" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {getFilteredAppointments().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Event sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nu ai programari viitoare
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Creeaza o programare noua pentru animalul tau
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleStartAppointmentRequest}
                disabled={pets.length === 0}
              >
                Alege clinica pentru programare
              </Button>
            </Box>
          ) : (
            <List>
              {getFilteredAppointments().map((apt) => (
                <ListItem
                  key={apt.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 2,
                    flexDirection: 'column',
                    alignItems: 'stretch'
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'start', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {format(parseISO(apt.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                        </Typography>
                        <Chip 
                          label={statusLabels[apt.status] || apt.status} 
                          color={statusColors[apt.status] || 'default'}
                          size="small"
                        />
                        {apt.emergency && (
                          <Chip label="Urgenta" color="error" size="small" />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Pets fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>{apt.patient.name}</strong> ({apt.patient.species})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospital fontSize="small" color="action" />
                          <Typography variant="body2">
                            {apt.clinic.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">
                            {apt.doctor.name}
                          </Typography>
                        </Box>
                        {apt.reason && (
                          <Typography variant="body2" color="text.secondary">
                            Motiv: {apt.reason}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleOpenDetails(apt)}
                      >
                        Detalii
                      </Button>
                      {canCancelAppointment(apt) && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => handleOpenReschedule(apt)}
                          >
                            Reprogrameaza
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setAppointmentToCancel(apt);
                              setCancelDialogOpen(true);
                            }}
                          >
                            Anuleaza
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {getFilteredAppointments().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AccessTime sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Nu ai programari in asteptare
              </Typography>
            </Box>
          ) : (
            <List>
              {getFilteredAppointments().map((apt) => (
                <ListItem key={apt.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                  <ListItemText
                    primary={format(parseISO(apt.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                    secondary={`${apt.patient.name} - ${apt.clinic.name}`}
                  />
                  <Chip label="In desfasurare" color="warning" />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {getFilteredAppointments().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CalendarMonth sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Nu ai programari in istoric
              </Typography>
            </Box>
          ) : (
            <List>
              {getFilteredAppointments().map((apt) => (
                <ListItem
                  key={apt.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 2,
                    flexDirection: 'column',
                    alignItems: 'stretch'
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'start', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {format(parseISO(apt.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                        </Typography>
                        <Chip 
                          label={statusLabels[apt.status] || apt.status} 
                          color={statusColors[apt.status] || 'default'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Pets fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>{apt.patient.name}</strong> ({apt.patient.species})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospital fontSize="small" color="action" />
                          <Typography variant="body2">
                            {apt.clinic.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">
                            {apt.doctor.name}
                          </Typography>
                        </Box>
                        {apt.reason && (
                          <Typography variant="body2" color="text.secondary">
                            Motiv: {apt.reason}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => handleOpenDetails(apt)}
                    >
                      Detalii
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
      </Card>

      {/* Dialog pentru creare programare */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          resetCreateDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Programare Noua</Typography>
            <IconButton onClick={() => {
              setCreateDialogOpen(false);
              resetCreateDialog();
            }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Selecteaza animalul */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                Pentru care animal doresti sa programezi consultatia?
              </Typography>
              <Grid container spacing={2}>
                {pets.map((pet) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={pet.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedPet === pet.id ? 2 : 1,
                        borderColor: selectedPet === pet.id ? 'primary.main' : 'divider',
                        '&:hover': { boxShadow: 3 }
                      }}
                      onClick={() => setSelectedPet(pet.id)}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Pets sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {pet.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pet.species} • {pet.breed}
                          </Typography>
                        </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            </Box>
          )}

          {/* Step 2: Alege clinica si medicul */}
          {activeStep === 1 && (
            <Box>
              <TextField
                fullWidth
                select
                label="Clinica"
                value={selectedClinic}
                onChange={(e) => setSelectedClinic(e.target.value)}
                sx={{ mb: 3 }}
              >
                {clinics.map((clinic) => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    <Box>
                      <Typography variant="body1">{clinic.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {clinic.address}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                select
                label="Medic veterinar"
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                disabled={!selectedClinic}
              >
                {doctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    <Box>
                      <Typography variant="body1">{doctor.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doctor.specialty}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {/* Step 3: Selecteaza data si ora */}
          {activeStep === 2 && (
            <Box>
              <TextField
                fullWidth
                type="date"
                label="Data"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
                sx={{ mb: 3 }}
              />

              {loadingSlots ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : availableSlots.length > 0 ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Sloturi disponibile:
                  </Typography>
                  <Grid container spacing={1}>
                    {availableSlots.map((slot, idx) => (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }} key={idx}>
                        <Button
                          fullWidth
                          variant={selectedTime === slot.start ? 'contained' : 'outlined'}
                          onClick={() => setSelectedTime(slot.start)}
                          sx={{ py: 1.5 }}
                        >
                          {format(parseISO(slot.start), 'HH:mm')}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : selectedDate ? (
                <Alert severity="info">
                  Nu sunt sloturi disponibile pentru data selectata
                </Alert>
              ) : null}

              <TextField
                fullWidth
                label="Motiv consultatie (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
                sx={{ mt: 3 }}
              />
            </Box>
          )}

          {/* Step 4: Confirma */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirma programarea
              </Typography>
              <Card sx={{ bgcolor: 'background.default', p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Animal
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>{pets.find(p => p.id === selectedPet)?.name}</strong>
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Clinica
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>{clinics.find(c => c.id === selectedClinic)?.name}</strong>
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Medic
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>{doctors.find(d => d.id === selectedDoctor)?.name}</strong>
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Data si ora
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>
                    {selectedDate && selectedTime && 
                      format(parseISO(selectedTime), 'dd MMMM yyyy, HH:mm', { locale: ro })
                    }
                  </strong>
                </Typography>

                {reason && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                      Motiv
                    </Typography>
                    <Typography variant="body1">
                      {reason}
                    </Typography>
                  </>
                )}
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetCreateDialog();
          }}>
            Anuleaza
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Inapoi
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !selectedPet) ||
                (activeStep === 1 && (!selectedClinic || !selectedDoctor)) ||
                (activeStep === 2 && (!selectedDate || !selectedTime))
              }
            >
              Continua
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCreateAppointment}>
              Confirma Programarea
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog pentru anulare programare */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Anuleaza Programarea</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Programarile pot fi anulate doar cu minimum 24 ore inainte.
          </Alert>
          {appointmentToCancel && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Esti sigur ca vrei sa anulezi programarea pentru{' '}
              <strong>{appointmentToCancel.patient.name}</strong> din data de{' '}
              <strong>{format(parseISO(appointmentToCancel.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}</strong>?
            </Typography>
          )}
          <TextField
            fullWidth
            label="Motiv anulare (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Renunta
          </Button>
          <Button variant="contained" color="error" onClick={handleCancelAppointment}>
            Confirma Anularea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pentru detalii programare */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Detalii Programare</Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAppointmentDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={statusLabels[selectedAppointmentDetails.status] || selectedAppointmentDetails.status} 
                    color={statusColors[selectedAppointmentDetails.status] || 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                  {selectedAppointmentDetails.emergency && (
                    <Chip label="Urgenta" color="error" sx={{ ml: 1, fontWeight: 600 }} />
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Data si ora
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  {format(parseISO(selectedAppointmentDetails.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Animal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Pets color="action" />
                  <Typography variant="body1">
                    <strong>{selectedAppointmentDetails.patient.name}</strong> - {selectedAppointmentDetails.patient.species}
                    {selectedAppointmentDetails.patient.breed && `, ${selectedAppointmentDetails.patient.breed}`}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Clinica
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LocalHospital color="action" />
                  <Box>
                    <Typography variant="body1">
                      <strong>{selectedAppointmentDetails.clinic.name}</strong>
                    </Typography>
                    {selectedAppointmentDetails.clinic.address && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedAppointmentDetails.clinic.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Medic veterinar
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Person color="action" />
                  <Typography variant="body1">
                    <strong>{selectedAppointmentDetails.doctor.name}</strong>
                  </Typography>
                </Box>
              </Box>

              {selectedAppointmentDetails.reason && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Motiv consultatie
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedAppointmentDetails.reason}
                  </Typography>
                </Box>
              )}

              {selectedAppointmentDetails.notes && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Note
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedAppointmentDetails.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Inchide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pentru reprogramare */}
      <Dialog 
        open={rescheduleDialogOpen} 
        onClose={() => setRescheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Cere Reprogramare</Typography>
            <IconButton onClick={() => setRescheduleDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Cererea ta de reprogramare va fi trimisa catre clinica pentru aprobare. Vei fi notificat cand clinica aproba sau respinge cererea.
          </Alert>

          {appointmentToReschedule && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Programare curenta
              </Typography>
              <Card sx={{ bgcolor: 'background.default', p: 2 }}>
                <Typography variant="body2">
                  <strong>{appointmentToReschedule.patient.name}</strong> la{' '}
                  <strong>{appointmentToReschedule.clinic.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(parseISO(appointmentToReschedule.start_ts), 'dd MMMM yyyy, HH:mm', { locale: ro })}
                </Typography>
              </Card>
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
            Selecteaza noua data si ora
          </Typography>

          <TextField
            fullWidth
            type="date"
            label="Data noua"
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
            sx={{ mb: 3 }}
          />

          {loadingRescheduleSlots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : rescheduleSlotsAvailable.length > 0 ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Sloturi disponibile:
              </Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {rescheduleSlotsAvailable.map((slot, idx) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={idx}>
                    <Button
                      fullWidth
                      variant={rescheduleTime === slot.start ? 'contained' : 'outlined'}
                      onClick={() => setRescheduleTime(slot.start)}
                      sx={{ py: 1.5 }}
                    >
                      {format(parseISO(slot.start), 'HH:mm')}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : rescheduleDate ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Nu sunt sloturi disponibile pentru data selectata
            </Alert>
          ) : null}

          <TextField
            fullWidth
            label="Motiv reprogramare"
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            multiline
            rows={3}
            placeholder="Ex: Nu pot ajunge la ora stabilita, am o alta programare, etc."
            helperText="Te rugam sa specifici motivul reprogramarii pentru a ajuta clinica sa proceseze cererea"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)}>
            Anuleaza
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRequestReschedule}
            disabled={!rescheduleTime || !rescheduleReason.trim()}
          >
            Trimite Cerere
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyAppointments;

